import { Button, Checkbox } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import React from "react";
import { Link } from "react-router-dom";

import { PaginatedSearch, WorkbookGridDto, WorkbookRequestApi } from "../../../../../api";
import { CustomDataTable } from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";

export interface IWorkbookSelectionPanelProps {
  onWorkbookSelected: (workbookId: number) => void;
  workbookRequestId: number;
}

export const WorkbookSelectionPanel: React.FunctionComponent<IWorkbookSelectionPanelProps> = ({
  onWorkbookSelected,
  workbookRequestId,
}) => {
  const api = useApiService(WorkbookRequestApi);
  const { currentLanguage } = useGlobalData();
  const { t } = useTl();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
  });

  const searchFunction = React.useCallback(
    (sObj?: PaginatedSearch) => api.getWorkbooks(workbookRequestId, sObj),
    [api, workbookRequestId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: WorkbookGridDto) => (
          <ButtonContainer>
            <Button
              onClick={() => {
                onWorkbookSelected(row.workbookId);
              }}
              text={t(ETLCodes.Select)}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Identifier),
        fieldName: "identifier",
        render: (row: WorkbookGridDto) => <div>{row.identifier > 0 ? row.identifier : ""}</div>,
      },
      {
        header: () => t(ETLCodes.Counter),
        fieldName: "counter",
        render: (row: WorkbookGridDto) => <div>{row.counter > 0 ? row.counter : ""}</div>,
      },
      {
        header: () => t(ETLCodes.WorkbookType),
        fieldName: "workbookType",
        render: (row: WorkbookGridDto) => (
          <Link target="_blank" to={`${ERoutes.workbookType}/${row.workbookTypeId}/detail/view`}>
            {row.workbookType}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.WithPedigree),
        fieldName: "withPedigree",
        alignment: "center",
        autoFitContent: true,
        render: (row: WorkbookGridDto) => <Checkbox checked={row.withPedigree} disabled />,
      },
      {
        header: () => t(ETLCodes.DeliveredOn),
        fieldName: "deliveredOn",
      },
    ],
    [onWorkbookSelected, t]
  );
  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);
  return (
    <CustomDataTable
      tableState={tableState}
      loading={loading}
      columns={columns}
      filterMode="OnEnter"
      focusOnFilter={true}
    />
  );
};
